"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DealDocumentsSchema = exports.DealDocumentSectionSchema = exports.DealDocumentLineSchema = exports.DEAL_DOCUMENT_SECTIONS = void 0;
exports.dealDocumentsConfigured = dealDocumentsConfigured;
exports.resolveDealDocuments = resolveDealDocuments;
exports.dealSectionAgreementIds = dealSectionAgreementIds;
exports.planDealPaperwork = planDealPaperwork;
exports.dealDocumentIds = dealDocumentIds;
const zod_1 = require("zod");
const documents_1 = require("./documents");
/**
 * DEAL DOCUMENTS — the paperwork a deal sells under (2026-08-30).
 *
 * THE MOVE THIS ENCODES. Documents used to be chosen per device finance
 * offer, on the financing wizard's agreement step (`BillingSku.
 * agreementDocumentIds`). That put a legal decision on a hardware SKU, where
 * it could only ever say one thing for every customer who ever bought that
 * terminal — and said nothing at all about the software half of the sale.
 * A deal is where a customer's terms are actually negotiated, so a deal is
 * where its paperwork now lives: hardware and software separately, because
 * they are different contracts signed by the same person on the same day.
 *
 * `BillingSku.agreementDocumentIds` is NOT removed and NOT deprecated out of
 * existence — offers already carry values in it and orders already signed
 * against it. Checkout reads the deal FIRST and falls back to the SKU
 * (`dealDocumentsConfigured` is that switch), so an un-migrated deal behaves
 * exactly as it does today and nothing has to be re-homed before this ships.
 *
 * WHY A FLAT SHAPE AND NOT THE BINDING REGISTRY. `documentBindings.ts` binds
 * documents to a SUBJECT ("what does a K10 owe?") and is the right model for
 * rules that hold across every sale of a thing. A deal's paperwork is the
 * opposite: it is per-negotiation and short-lived, edited on the deal screen
 * and saved with the deal in one write. Routing it through the registry would
 * make saving a deal a cross-service write that can half-fail, for no gain —
 * the two mechanisms answer different questions and both resolve into the
 * same checkout list.
 */
exports.DEAL_DOCUMENT_SECTIONS = ['hardware', 'software'];
/** Caps mirror `DocumentBindingSchema.items` (40) — a deal cannot grow an unbounded binder. */
const MAX_PER_LIST = 40;
const MAX_CUSTOM_LINES = 40;
/**
 * A LINE THE OPERATOR NAMED THEMSELVES.
 *
 * `label` is what the customer reads at checkout; `documentId` is what they
 * actually sign or accept. They are deliberately separate: "Buffalo site
 * exclusivity" is what this deal calls it, while the document behind it is
 * titled "Exclusivity Rider" and is shared with four other deals. Renaming a
 * line on one deal must never rename the document under everyone else.
 */
exports.DealDocumentLineSchema = zod_1.z.object({
    /** Stable within one deal — the key a UI edits and removes by. */
    lineId: zod_1.z.string().min(1).max(64),
    label: zod_1.z.string().min(1).max(120),
    kind: zod_1.z.enum(documents_1.DOCUMENT_KINDS),
    documentId: zod_1.z.string().min(1),
    section: zod_1.z.enum(exports.DEAL_DOCUMENT_SECTIONS),
    /**
     * A required line blocks checkout until it is discharged; an optional one is
     * shown and can be skipped. Defaults true — a document nobody has to read is
     * the exception, and the safe default for paperwork is that it counts.
     */
    required: zod_1.z.boolean().default(true),
});
/** One section's two lists. Agreements are signed; policies are accepted. */
exports.DealDocumentSectionSchema = zod_1.z.object({
    agreementDocumentIds: zod_1.z.array(zod_1.z.string().min(1)).max(MAX_PER_LIST).default([]),
    policyDocumentIds: zod_1.z.array(zod_1.z.string().min(1)).max(MAX_PER_LIST).default([]),
});
exports.DealDocumentsSchema = zod_1.z.object({
    hardware: exports.DealDocumentSectionSchema.optional(),
    software: exports.DealDocumentSectionSchema.optional(),
    /**
     * Spans both sections, so it sits on neither. A policy, always — a refund
     * term is disclosed and accepted, never counter-signed.
     */
    returnRefundPolicyDocumentId: zod_1.z.string().min(1).optional(),
    customLines: zod_1.z.array(exports.DealDocumentLineSchema).max(MAX_CUSTOM_LINES).optional(),
});
/**
 * Has this deal been given its own paperwork?
 *
 * THE FALLBACK SWITCH, and deliberately a whole-object question rather than a
 * per-section one. A deal that names software agreements but no hardware ones
 * has ANSWERED for its hardware — the answer is "nothing" — and falling back
 * to the SKU for that half would resurrect documents the operator removed on
 * purpose. Half-deal-half-SKU is the one combination that can produce a
 * contract nobody chose.
 */
function dealDocumentsConfigured(docs) {
    if (!docs)
        return false;
    return Boolean(docs.hardware?.agreementDocumentIds?.length
        || docs.hardware?.policyDocumentIds?.length
        || docs.software?.agreementDocumentIds?.length
        || docs.software?.policyDocumentIds?.length
        || docs.returnRefundPolicyDocumentId
        || docs.customLines?.length);
}
/** Agreements before policies within a section; hardware before software overall. */
const SECTION_RANK = { hardware: 0, software: 1 };
const KIND_RANK = { agreement: 0, policy: 1 };
/**
 * Flattens a deal's paperwork into the ordered, de-duplicated list checkout
 * renders.
 *
 * PURE and total — same input, same output, no I/O — so the deal screen's
 * preview and the checkout that binds the signatures are the same code rather
 * than two implementations that agree until they do not.
 *
 * RULES, each of which exists because the alternative is a wrong contract:
 *
 *  - A RETIRED or UNKNOWN document is DROPPED, and named in `dropped`. It is
 *    never silently included (nobody may be asked to sign a withdrawn
 *    contract) and never silently omitted (an operator whose deal quietly lost
 *    its rental agreement must be told). The caller decides how loud to be.
 *  - A document appearing twice collapses to ONE line. The strongest claim
 *    wins: `required` if any occurrence is required, and a custom line's
 *    `label` beats the plain document title, because the operator typed it on
 *    purpose. Two lines for one document would ask for the same signature
 *    twice.
 *  - A document's `kind` comes from the CATALOGUE, not from the list it was
 *    found in. Filing a policy under "agreements to sign" must not turn it
 *    into something that demands a signature.
 */
function resolveDealDocuments(docs, catalogue) {
    const byId = new Map(catalogue.map((c) => [c.documentId, c]));
    const out = new Map();
    const dropped = [];
    /**
     * Tie-break tier, kept out of the public shape because it is an ordering
     * detail and not something a caller should branch on. Everything is tier 0
     * except the refund policy, which is tier 1 so it reads last — it is the
     * closing term of the sale, not one item among the software policies it
     * would otherwise sort into alphabetically.
     */
    const tier = new Map();
    const add = (documentId, section, required, label, rank = 0) => {
        const entry = byId.get(documentId);
        if (!entry || entry.status !== 'active') {
            if (!dropped.includes(documentId))
                dropped.push(documentId);
            return;
        }
        const existing = out.get(documentId);
        if (existing) {
            // Strongest claim wins — see the doc comment above. A document reached by
            // two routes takes the EARLIER tier, so naming the refund policy again as
            // a custom line promotes it back in with the rest rather than stranding it.
            existing.required = existing.required || required;
            tier.set(documentId, Math.min(tier.get(documentId) ?? rank, rank));
            if (label && !existing.named) {
                existing.label = label;
                existing.named = true;
            }
            return;
        }
        tier.set(documentId, rank);
        out.set(documentId, {
            documentId,
            label: label || entry.title,
            kind: entry.kind,
            section,
            required,
            named: Boolean(label),
        });
    };
    for (const section of exports.DEAL_DOCUMENT_SECTIONS) {
        const cfg = docs?.[section];
        for (const id of cfg?.agreementDocumentIds ?? [])
            add(id, section, true);
        for (const id of cfg?.policyDocumentIds ?? [])
            add(id, section, true);
    }
    // The refund policy spans both halves, so it is filed under software and
    // given the trailing tier — it closes the list rather than interrupting it.
    if (docs?.returnRefundPolicyDocumentId)
        add(docs.returnRefundPolicyDocumentId, 'software', true, undefined, 1);
    for (const line of docs?.customLines ?? [])
        add(line.documentId, line.section, line.required, line.label);
    const items = [...out.values()].sort((a, b) => (SECTION_RANK[a.section] - SECTION_RANK[b.section]
        || KIND_RANK[a.kind] - KIND_RANK[b.kind]
        || (tier.get(a.documentId) ?? 0) - (tier.get(b.documentId) ?? 0)
        || a.label.localeCompare(b.label)));
    return { items, dropped };
}
/**
 * THE AGREEMENTS ONE SECTION OWES — ids only, NO CATALOGUE NEEDED.
 *
 * `resolveDealDocuments` is the richer answer, but it requires a catalogue to
 * establish each document's kind, title and status. Checkout in billing-service
 * has no catalogue to hand and does not need one: the deal filed these ids
 * under "agreements" itself, and documents-service refuses to instantiate a
 * retired or unpublished document anyway — so the check that matters happens at
 * the only place that can actually make it.
 *
 * Custom lines are included when they name this section AND declare
 * `kind: 'agreement'`. Order is preserved and duplicates collapse, so the
 * result is stable enough to key an idempotency token on.
 */
function dealSectionAgreementIds(docs, section) {
    const ids = new Set(docs?.[section]?.agreementDocumentIds ?? []);
    for (const line of docs?.customLines ?? []) {
        if (line.section === section && line.kind === 'agreement')
            ids.add(line.documentId);
    }
    return [...ids];
}
/**
 * THE PAPERWORK ONE CHECKOUT PRESENTS, split by how it is signed.
 *
 * `combined` — the device paperwork for EVERY UNIT on the order, then the
 * software paperwork. The customer opens each piece and signs the lot ONCE.
 * Per unit is the point: two of the same terminal are two agreements, each
 * naming its own unit, because that is what the merchant is actually buying
 * and what a later dispute is about.
 *
 * `standalone` — the deal-wide lines: the return & refund policy and every
 * custom line. These are signed ONE BY ONE and are never folded into the
 * combined packet.
 *
 * A DOCUMENT NEVER APPEARS TWICE. A deal that names the same document in its
 * hardware section AND as a custom line asks for one signature, not two — the
 * per-unit copy wins, because it is the more specific claim. (The deal seeded
 * in dev does exactly this, which is how the rule got written.)
 *
 * PURE and total, like `resolveDealDocuments` beside it, so the deal screen's
 * preview and the checkout that binds the signatures are the same code.
 *
 * NO CATALOGUE, deliberately — same reasoning as `dealSectionAgreementIds`:
 * the deal filed each id under agreements or policies itself, and
 * documents-service refuses to instantiate a retired or unpublished document
 * anyway. `titleOf` lets a caller that HAS titles supply them; without it the
 * id is used, which is ugly but never wrong.
 */
function planDealPaperwork(docs, units, titleOf = (id) => id, dischargeFor = (_id, kind) => (kind === 'agreement' ? 'signature' : 'accept_decline')) {
    const combined = [];
    const standalone = [];
    const claimed = new Set();
    /** Agreements before policies, so the list reads the way the sale does. */
    const sectionIds = (section) => [
        ...(docs?.[section]?.agreementDocumentIds ?? []).map((id) => ({ id, kind: 'agreement' })),
        ...(docs?.[section]?.policyDocumentIds ?? []).map((id) => ({ id, kind: 'policy' })),
    ];
    // ── device: the hardware section, once per UNIT ──────────────────────
    for (const unit of units) {
        for (const { id, kind } of sectionIds('hardware')) {
            claimed.add(id);
            combined.push({
                documentId: id, kind, discharge: dischargeFor(id, kind),
                group: 'device', unitRef: unit.ref, required: true,
                label: `${unit.label} · ${titleOf(id)}`,
            });
        }
    }
    // ── software: once for the order ─────────────────────────────────────
    for (const { id, kind } of sectionIds('software')) {
        claimed.add(id);
        combined.push({
            documentId: id, kind, discharge: dischargeFor(id, kind),
            group: 'software', required: true, label: titleOf(id),
        });
    }
    // ── deal-wide: each signed on its own ────────────────────────────────
    /*
      A DEAL-WIDE LINE IS ITS OWN OBLIGATION, even when the same document also
      appears per device.
  
      This used to drop any deal-wide line whose document was already claimed
      elsewhere, on the reasoning that nobody should sign one document twice. In
      practice operators file the SAME document in the hardware section and as the
      return policy on purpose — those are different promises about the same
      text — and the dedupe silently deleted the whole deal-wide group. An
      operator who had configured site-wide paperwork saw none of it, with nothing
      to say why.
  
      Deduping still happens WITHIN the deal-wide group (a document named twice
      there is one line), which is the only place it was ever unambiguous.
    */
    const claimedStandalone = new Set();
    const addStandalone = (documentId, kind, label, required) => {
        if (claimedStandalone.has(documentId))
            return;
        claimedStandalone.add(documentId);
        standalone.push({ documentId, kind, discharge: dischargeFor(documentId, kind), group: 'deal', label, required });
    };
    if (docs?.returnRefundPolicyDocumentId) {
        const id = docs.returnRefundPolicyDocumentId;
        addStandalone(id, 'policy', titleOf(id), true);
    }
    for (const line of docs?.customLines ?? []) {
        // The operator typed this label on purpose; it beats the document title.
        addStandalone(line.documentId, line.kind, line.label || titleOf(line.documentId), line.required);
    }
    return { combined, standalone };
}
/**
 * Every document id a deal names, in no particular order and without needing a
 * catalogue — what a loader fetches before it can call `resolveDealDocuments`.
 */
function dealDocumentIds(docs) {
    const ids = new Set();
    for (const section of exports.DEAL_DOCUMENT_SECTIONS) {
        for (const id of docs?.[section]?.agreementDocumentIds ?? [])
            ids.add(id);
        for (const id of docs?.[section]?.policyDocumentIds ?? [])
            ids.add(id);
    }
    if (docs?.returnRefundPolicyDocumentId)
        ids.add(docs.returnRefundPolicyDocumentId);
    for (const line of docs?.customLines ?? [])
        ids.add(line.documentId);
    return [...ids];
}
//# sourceMappingURL=dealDocuments.js.map